import React, { useEffect, useState } from 'react';
import { dialogConfigs } from '../Utils/PMHelper';
import { TEnvironment } from '../Types/channel.types';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Button from 'Components/DesignSystem/Button/Button';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Typography from 'Components/DesignSystem/Typography/Typography';

type AddEnvironmentDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (data: any) => void;
    isLoading?: boolean;
    editData?: TEnvironment | null;
    mode?: 'create' | 'edit';
    hasProductionEnv?: boolean;
    id?: string;
}

/**
 * Add / Edit Environment on the design-system `Dialog` (§ 120).
 *
 * **This was `CustomDialog`'s last consumer, and its only checkbox one** — the
 * `type: 'checkbox'` branch of that generic form renderer existed for this one field.
 * With this migration `CustomDialog` has no callers left; deleting it is listed as
 * owed rather than done here, because it still has its own test file and the removal
 * belongs in a change of its own.
 *
 * The production-flag rules are preserved exactly, and they are subtle:
 *
 * - **Create:** the checkbox is disabled when a production environment already exists.
 * - **Edit:** disabled only when another environment is production
 *   (`hasProductionEnv && !editData.isProduction`) — so the environment that IS
 *   production can still be un-set.
 *
 * The label carries a quoted phrase — "Use this as 'Production Environment'" — which
 * `CustomDialog` rendered through a `split(/('.*?')/)` that bolded the quoted run.
 * That formatting is kept, because the emphasis is what makes the sentence scan.
 *
 * Behaviour otherwise unchanged: `onSave` receives `{ environmentName, useAsProduction }`
 * for the parent to map, and no field is validated.
 */
export const AddEnvironmentDialog = ({ isOpen, onClose, onSave, isLoading = false, editData = null, mode = 'create', hasProductionEnv = false, id }: AddEnvironmentDialogProps) => {
    const [environmentName, setEnvironmentName] = useState('');
    const [useAsProduction, setUseAsProduction] = useState(false);

    const baseId = id || `environment-${mode}-dialog`;

    /* `CustomDialog` derived both from `field.defaultValue`; same values, same timing
       (on open only — see § 119). */
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (mode === 'edit' && editData) {
            setEnvironmentName(editData.name);
            setUseAsProduction(Boolean(editData.isProduction));
        } else {
            setEnvironmentName('');
            setUseAsProduction(false);
        }
    }, [isOpen]);

    /* Verbatim from the descriptor's two branches — see the docblock. */
    const productionDisabled = mode === 'edit' && editData
        ? hasProductionEnv && !editData.isProduction
        : hasProductionEnv;

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    return (
        <Dialog
            isOpen={isOpen}
            onClose={handleClose}
            title={mode === 'edit' ? 'Edit Environment' : dialogConfigs.addEnvironment.title}
            size="md"
            data-testid={baseId}
            footer={
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
                    <Button
                        variant="secondary"
                        id={`${baseId}-cancel-btn`}
                        data-testid={`${baseId}-cancel-btn`}
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        id={`${baseId}-save-btn`}
                        data-testid={`${baseId}-save-btn`}
                        onClick={() => onSave({ environmentName, useAsProduction })}
                        /* § 209 — loading implies the disabled gate this carried,
                           plus the spinner, aria-busy and the wait cursor. */
                        loading={isLoading}
                        className="tw-min-w-[92px]"
                    >
                        {isLoading ? 'Creating...' : (mode === 'edit' ? 'Update' : 'Create')}
                    </Button>
                </div>
            }
        >
            <div className="tw-space-y-3">
                <TextField
                    label="Environment Name"
                    required
                    placeholder="Enter Environment Name"
                    value={environmentName}
                    onChange={setEnvironmentName}
                    disabled={isLoading}
                    id={`${baseId}-name`}
                    data-testid={`${baseId}-name`}
                />
                <Checkbox
                    label={
                        <Typography variant="body-primary" as="span" className="tw-text-ink">
                            Use this as <span className="tw-font-semibold">&apos;Production Environment&apos;</span>
                        </Typography>
                    }
                    checked={useAsProduction}
                    onChange={setUseAsProduction}
                    disabled={isLoading || productionDisabled}
                    id={`${baseId}-production`}
                    data-testid={`${baseId}-production`}
                />
            </div>
        </Dialog>
    );
};
